import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditDialog, ExposedFunctions } from "../../framework/editDialog";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Button, TextField } from "@mui/material";
import { STYLE_CONSTANTS } from "../../../framework/theme";
import { defaultFormikConfig, formikFieldProps } from "../../framework/formUtils";
import { MaintenanceReminderStatesEditAllowed, MaintenanceReminderStatus, VehicleMaintenanceReminderDto } from "../../../models/vehicle/vehicleMaintenanceReminder";
import { useFormik } from "formik";
import * as yup from "yup";
import { Checkbox } from "@mui/material";
import { useAppDispatch } from "../../../framework/customStore";
import { showSuccessMessage } from "../../../models/store/storeActions";
import { deleteVehicleMaintenanceReminderData, saveVehicleMaintenanceReminder } from "../../../store/vehicleSlice";
import { VehicleMaintenanceDetailsListItem } from "../maintenanceView/maintenanceView";
import { MuiDatePicker } from "../../framework/muiDatepicker";
import { Base } from "../../../framework/base";

interface MaintenanceReminderDialogProps {
    isEdit: boolean;
    maintenanceItem: VehicleMaintenanceDetailsListItem;
    maintenanceReminderItem?: VehicleMaintenanceReminderDto;
    open: boolean;
    onClose: () => void;
    fetchVehicleMaintenances?: () => void;
}

export const MaintenanceReminderDialog = ({
    isEdit,
    maintenanceItem,
    maintenanceReminderItem,
    open,
    onClose,
    fetchVehicleMaintenances,
}: MaintenanceReminderDialogProps) => {
    const dialogRef = useRef<ExposedFunctions>();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const isEditDisabled = maintenanceReminderItem?.id &&
        !MaintenanceReminderStatesEditAllowed.some(x => x === maintenanceReminderItem?.status);

    const [isRemindByDateOn, setIsRemindByDateOn] =
        useState<boolean>(false);
    const [isRemindByOdometerReadingOn, setIsRemindByOdometerReadingOn] =
        useState<boolean>(false);

    const emptyRow: VehicleMaintenanceReminderDto = {
        id: null,
        vehicleMaintenanceId: maintenanceItem?.id,
        status: MaintenanceReminderStatus.Incoming,
        remindByDate: false,
        remindByOdometerReading: false,
        datesBeforeReminderStarts: null,
        odometerReadingForMaintenance: null,
        distanceBeforeReminderStarts: null,
        reminderDate: null,
    };

    const item = maintenanceReminderItem ?
        maintenanceReminderItem : emptyRow;

    const handleSubmit = async(
        values: VehicleMaintenanceReminderDto
    ): Promise<VehicleMaintenanceReminderDto> => {
        return new Promise((resolve, reject) => {
            const isNew = values?.id === null;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            dispatch(saveVehicleMaintenanceReminder({ vehicleMaintenanceReminderDto: values }) as any)
                .then((action : {payload: VehicleMaintenanceReminderDto}) => {
                    const message = isNew ? t("vehicleMaintenanceReminder.maintenanceAddedSuccessfully") : t("vehicleMaintenanceReminder.maintenanceEditedSuccessfully");
                    dispatch(showSuccessMessage(message));
                    resolve(action.payload);
                    if (fetchVehicleMaintenances) {
                        fetchVehicleMaintenances();
                    }
                    onClose();
                }).catch((error) => {
                    const message = isNew ? t("vehicleMaintenanceReminder.saveFailed") : t("vehicleMaintenanceReminder.editFailed");
                    console.error("Error saving vehicle maintenance: ", error);
                    reject(new Error(message));
                });
        });
    };

    const formik = useFormik<VehicleMaintenanceReminderDto>({
        ...defaultFormikConfig,
        enableReinitialize: true,
        initialValues: item,
        validationSchema: yup.object({
            reminderDate: isRemindByDateOn ?
                yup.string().nullable().required(
                    t("validation.fieldIsRequired", {
                        field: t("vehicleMaintenanceReminder.reminderDate"),
                    })
                ) :
                yup.string().nullable(),
            datesBeforeReminderStarts: isRemindByDateOn ?
                yup.number().nullable().required(
                    t("validation.fieldIsRequired", {
                        field: t("vehicleMaintenanceReminder.datesBeforeReminderStarts"),
                    })
                ) :
                yup.number().nullable(),
            odometerReadingForMaintenance: isRemindByOdometerReadingOn ?
                yup.number().max(2000000).nullable().required(
                    t("validation.fieldIsRequired", {
                        field: t("vehicleMaintenanceReminder.odometerReadingForMaintenance"),
                    })
                ) :
                yup.number().max(1999999).nullable(),
            distanceBeforeReminderStarts: isRemindByOdometerReadingOn ?
                yup.number().max(25000).nullable().required(
                    t("validation.fieldIsRequired", {
                        field: t("vehicleMaintenanceReminder.distanceBeforeReminderStarts"),
                    })
                ) :
                yup.number().max(25000).nullable(),
        }),
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        setIsRemindByDateOn(formik?.values.remindByDate);
    }, [formik?.values.remindByDate]);
    useEffect(() => {
        setIsRemindByOdometerReadingOn(formik?.values.remindByOdometerReading);
    }, [formik?.values.remindByOdometerReading]);

    return (
        <>
            <EditDialog
                loading={!maintenanceItem}
                open={open}
                title={isEdit ? t("common.edit") : t("vehicleMaintenanceReminder.addNew")}
                formik={formik}
                onClose={onClose}
                editDisabled={isEditDisabled || (!isRemindByDateOn && !isRemindByOdometerReadingOn)}
                enableDefaultActions
                ref={dialogRef}
                body={
                    <Grid2
                        container
                        direction="column"
                        spacing={STYLE_CONSTANTS.formSpacing}
                        sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                        <Grid2 xs={12}>
                            <label className={"field-width-normal"}>
                                <Checkbox
                                    id="remindByDate"
                                    checked={formik.values?.remindByDate}
                                    onChange={() => void formik.setFieldValue("remindByDate", !formik.values.remindByDate)}
                                    disabled={isEditDisabled}
                                />
                                {t("vehicleMaintenanceReminder.remindByDate")}
                            </label>
                        </Grid2>
                        <Grid2 xs={12}>
                            <label className={"field-width-normal"}>
                                <Checkbox
                                    id="remindByOdometerReading"
                                    checked={formik.values?.remindByOdometerReading}
                                    onChange={() => void formik.setFieldValue("remindByOdometerReading", !formik.values.remindByOdometerReading)}
                                    disabled={isEditDisabled}
                                />
                                {t("vehicleMaintenanceReminder.remindByOdometerReading")}
                            </label>
                        </Grid2>
                        {isRemindByDateOn && (
                            <>
                                <Grid2 xs={12}>
                                    <MuiDatePicker
                                        id="reminderDate"
                                        label={t("vehicleMaintenanceReminder.reminderDate")}
                                        value={formik.values?.reminderDate ? new Date(formik.values.reminderDate) : null}
                                        onChange={(val) =>
                                            void formik.setFieldValue(
                                                "reminderDate",
                                                val ? Base.dayjsToJsonDate(val) : null
                                            )
                                        }
                                        disabled={isEditDisabled}
                                        extraProps={{ textFieldProps: { fullWidth: true } }}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <TextField
                                        id="datesBeforeReminderStarts"
                                        label={t("vehicleMaintenanceReminder.datesBeforeReminderStarts")}
                                        type="number"
                                        fullWidth
                                        {...formikFieldProps(formik, "datesBeforeReminderStarts")}
                                        value={formik.values?.datesBeforeReminderStarts ?? ""}
                                        disabled={isEditDisabled}
                                    />
                                </Grid2>
                            </>
                        )}
                        {isRemindByOdometerReadingOn && (
                            <>
                                <Grid2 xs={12}>
                                    <TextField
                                        id="odometerReadingForMaintenance"
                                        label={t("vehicleMaintenanceReminder.odometerReadingForMaintenance")}
                                        type="number"
                                        fullWidth
                                        {...formikFieldProps(formik, "odometerReadingForMaintenance")}
                                        value={formik.values?.odometerReadingForMaintenance ?? ""}
                                        disabled={isEditDisabled}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <TextField
                                        id="distanceBeforeReminderStarts"
                                        label={t("vehicleMaintenanceReminder.distanceBeforeReminderStarts")}
                                        type="number"
                                        fullWidth
                                        {...formikFieldProps(formik, "distanceBeforeReminderStarts")}
                                        value={formik.values?.distanceBeforeReminderStarts ?? ""}
                                        disabled={isEditDisabled}
                                    />
                                </Grid2>
                            </>
                        )}
                        {maintenanceItem?.vehicleMaintenanceReminder?.id && !isEditDisabled && (
                            <Grid2 xs={12}>
                                <Button
                                    color="error"
                                    fullWidth
                                    variant="contained"
                                    onClick={() => {
                                        const id = maintenanceItem?.vehicleMaintenanceReminder?.id;
                                        if (id) {
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                            void dispatch(
                                                deleteVehicleMaintenanceReminderData({ vehicleMaintenanceReminderId: id }) as any
                                            ).then(() => {
                                                dispatch(showSuccessMessage(t("vehicleMaintenanceReminder.maintenanceDeletedSuccessfully")));
                                                if (fetchVehicleMaintenances) {
                                                    fetchVehicleMaintenances();
                                                }
                                                onClose();
                                            });
                                        }
                                    }}
                                >
                                    {t("common.remove")}
                                </Button>
                            </Grid2>
                        )}
                    </Grid2>
                }
            />
        </>
    );
};
